import React, { useEffect, useRef, useState } from "react";
import {
  Grid,
  FormLabel,
  TextField,
  Typography,
  Button,
  FormControl,
  Input,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import Layout from "../../layout/Layout";
import * as Yup from "yup";
import MenuItem from "@mui/material/MenuItem";
import {
  phoneRegExp,
  insertCustomerAPI,
  fetchCustomerByIdAPI,
  updateCustomerByIdAPI,
  trnNumber,
  fetchStatusByParent,
} from "../../config";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { LoadingButton } from "@mui/lab";
import { useNavigate, useParams } from "react-router-dom";

import { emirates, isKnownEmirate } from "../../components/constants";
import { useFormik } from "formik";
import { AlertMessage } from "../../components/AlertMessage";
import { apiData } from "../../services/api_service";
import { Backdrop, CircularProgress } from "@mui/material";

// UAE-specific format requirement removed (Item 8) — only a loose, generic
// "digits, spaces, +, -" sanity check remains, and only when a value is
// actually entered. Contact Number is optional.
const validatePhone = (value) => {
  if (!value) return true;

  if (!/^[0-9+ ]+$/.test(value)) {
    return "Invalid Phone number";
  }

  return true;
};

const validationSchema = Yup.object().shape({
  customer_type: Yup.string().required("Customer type is required"),
  company_name: Yup.string().when("customer_type", {
    is: "Company",
    then: (schema) =>
      schema
        .required("Company Name is required")
        .min(2, "Minimum 2 characters")
        .max(150, "Maximum 150 characters"),
    otherwise: (schema) => schema.nullable().notRequired(),
  }),
  name: Yup.string()
    .required("Name is required")
    .min(3, "Minimum 3 characters")
    .max(100, "Maximum 100 characters")
    .matches(
      /^[a-zA-Z0-9 _-]+$/,
      "Only letters, numbers, spaces, hyphen (-) and underscore (_) are allowed"
    ),
  address: Yup.string()
    .required("Street Address is required")
    .min(3, "Minimum 3 characters")
    .max(100, "Maximum 100 characters")
    .matches(
      /^[a-zA-Z0-9 _,-]+$/,
      "Only letters, numbers, spaces, hyphen (-), comma (,) and underscore (_) are allowed"
    ),
  emirate: Yup.string()
    .required("Emirate/location is required")
    .min(3, "Emirate must be at least 3 characters")
    .max(100, "Emirate cannot be more than 100 characters"),

  phone_number: Yup.string()
    .nullable()
    .test("valid-phone", (value, ctx) => {
      const result = validatePhone(value);
      return result === true ? true : ctx.createError({ message: result });
    }),

  // Documents (Proforma/CDN/Invoice) print this customer field as "VAT No.",
  // not "TRN" (see [[trn-to-vat-no-rename]]) — the form field was swapped
  // from trn_number to vat_no to match, reusing the same 15-digit pattern.
  vat_no: Yup.string()
    .required("VAT No. is required")
    .matches(
      trnNumber,
      "Please enter a valid 15 digit VAT No."
    ),

  credit_days: Yup.number()
    .nullable()
    .transform((value, originalValue) =>
      String(originalValue).trim() === "" ? null : value
    )
    .typeError("Credit Days must be a number")
    .min(0, "Credit Days cannot be negative")
    .integer("Credit Days must be a whole number"),

  email: Yup.string()
    .nullable()
    .notRequired()
    .test("valid-email", "Enter a valid email address", (value) => {
      if (!value) return true;
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }),
});

const ManageCustomer = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const isAdmin = Number(user?.position) === 1;
  const navigate = useNavigate();
  const alertRef = useRef();
  const [status, setStatus] = useState([]);
  const { id } = useParams();
  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      customer_type: "Company",
      company_name: "",
      name: "",
      address: "",
      emirate: "",
      po_box: "",
      phone_number: "",
      email: "",
      vat_no: "",
      user_id: "",
      trn_number: "",
      status: 2,
      credit_days: "",
      emirateOption: "",
    },
    validationSchema,

    onSubmit: async (values) => {
      try {
        setLoading(true);
        const payload = { ...values, user_id: user.emp_id };

        // Only an Admin is allowed to send credit_days; other users create/update
        // customers without touching it so it stays untouched on the backend.
        if (!isAdmin) {
          delete payload.credit_days;
        }

        let result;
        if (id) {
          result = await apiData(
            "put",
            `${updateCustomerByIdAPI}/${id}`,
            payload
          );
        } else {
          result = await apiData("post", insertCustomerAPI, payload);
        }

        if (result.success) {
          alertRef.current.triggerAlert(
            JSON.stringify({
              message: result.msg,
              messageType: "success",
            })
          );

          setTimeout(() => {
            navigate("/customer/list"); // redirect to order list after save
          }, 3000);
        } else {
          alertRef.current.triggerAlert(
            JSON.stringify({
              message: result.msg,
              messageType: "error",
            })
          );
        }
      } catch (error) {
        alertRef.current.triggerAlert(
          JSON.stringify({
            message: error.msg,
            messageType: "error",
          })
        );
      } finally {
        setLoading(false)
      }
    },
  });

  useEffect(() => {
    const fetchStatus = async () => {
      const data = {
        intParentId: 1,
      };

      try {
        const result = await apiData("get", fetchStatusByParent, data);
        if (result.success) {
          setStatus(result.data);
        }
      } catch (error) {
        console.error("Failed to fetch units:", error);
      }
    };

    fetchStatus();
  }, []);

  useEffect(() => {
    if (id) {
      const fetchCustomer = async () => {
        try {
          const result = await apiData("get", `${fetchCustomerByIdAPI}/${id}`);
          if (result.success && result.data?.length > 0) {
            const customer = result.data[0];
            formik.setValues({
              customer_type: customer.customer_type || "Company",
              company_name: customer.company_name || "",
              name: customer.name || "",
              address: customer.address || "",
              emirate: customer.emirate || "",
              po_box: customer.po_box || "",
              phone_number: customer.phone_number || "",
              email: customer.email || "",
              vat_no: customer.vat_no || "",
              trn_number: customer.trn_number || "",
              status: customer.status,
              emirateOption: customer.emirate
                ? isKnownEmirate(customer.emirate)
                  ? customer.emirate
                  : "Others"
                : "",
              credit_days:
                customer.credit_days === null ||
                  customer.credit_days === undefined
                  ? ""
                  : customer.credit_days,
            });
          } else {
            alertRef.current.triggerAlert(
              JSON.stringify({
                message: "Customer not found",
                messageType: "error",
              })
            );
          }
        } catch (error) {
          alertRef.current.triggerAlert(
            JSON.stringify({ message: error.msg, messageType: "error" })
          );
        }
      };

      fetchCustomer();
    }
  }, [id]);

  return (
    <Layout>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Grid className="employee-form">
        <AlertMessage ref={alertRef} />
        <Grid container spacing={3}>
          <Grid
            textAlign="center"
            item
            className="form-lable"
            xs={12}
            lg={6}
            md={6}
          >
            <Typography className="page_title" textAlign="left">
              Manage Vendors
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Typography color="text.primary">Vendors</Typography>
              <Typography color="text.primary">Manage</Typography>
            </Breadcrumbs>
          </Grid>
          <Grid
            textAlign="right"
            item
            className="form-lable"
            xs={12}
            lg={6}
            md={6}
          >
            <LoadingButton
              size="medium"
              variant="contained"
              color="secondary"
              className="button_first"
              component={Link}
              href="/customer/list"
            >
              List Vendors
            </LoadingButton>
            <LoadingButton
              size="medium"
              variant="contained"
              color="primary"
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIosIcon />}
            >
              Back
            </LoadingButton>
          </Grid>
        </Grid>

        <form className="form_design" onSubmit={formik.handleSubmit}>
          <h3>Customer Details</h3>
          <Grid container spacing={2}>
            <Grid item xs={12} className="form-lable">
              <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1, color: "text.primary" }}>
                Customer Type *
              </FormLabel>
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  name="customer_type"
                  value={formik.values.customer_type}
                  onChange={(e) => {
                    const type = e.target.value;
                    formik.setFieldValue("customer_type", type);
                    if (type === "Individual") {
                      formik.setFieldValue("company_name", "");
                    }
                  }}
                >
                  <FormControlLabel value="Company" control={<Radio color="primary" />} label="Company" />
                  <FormControlLabel value="Individual" control={<Radio color="primary" />} label="Individual" />
                </RadioGroup>
              </FormControl>
            </Grid>

            {formik.values.customer_type === "Company" && (
              <Grid item className="form-lable" xs={12} lg={6} md={6}>
                <TextField
                  name="company_name"
                  label="Company Name *"
                  placeholder="Enter company name"
                  fullWidth
                  value={formik.values.company_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.company_name && Boolean(formik.errors.company_name)}
                  helperText={formik.touched.company_name && formik.errors.company_name}
                />
              </Grid>
            )}

            <Grid item className="form-lable" xs={12} lg={6} md={6}>
              <TextField
                name="name"
                label={formik.values.customer_type === "Company" ? "Contact Person Name *" : "Customer Name *"}
                placeholder={formik.values.customer_type === "Company" ? "Enter contact person name" : "Enter customer name"}
                fullWidth
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
              />
            </Grid>

            <Grid item className="form-lable" xs={12} lg={6} md={6}>
              <TextField
                name="address"
                label="Street Address"
                fullWidth
                value={formik.values.address}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.address && Boolean(formik.errors.address)}
                helperText={formik.touched.address && formik.errors.address}
              />
            </Grid>
            <Grid item className="form-lable" xs={12} lg={6} md={6}>
              <FormControl fullWidth>
                <TextField
                  name="emirateOption"
                  select
                  label="Emirate"
                  fullWidth
                  value={formik.values.emirateOption}
                  onChange={(e) => {
                    const value = e.target.value;
                    formik.setFieldValue("emirateOption", value);
                    formik.setFieldValue(
                      "emirate",
                      value === "Others" ? "" : value
                    );
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.emirate && Boolean(formik.errors.emirate)
                  }
                  helperText={formik.touched.emirate && formik.errors.emirate}
                >
                  {emirates.map((emirate) => (
                    <MenuItem key={emirate.id} value={emirate.name}>
                      {emirate.name}
                    </MenuItem>
                  ))}
                  <MenuItem value="Others">Others</MenuItem>
                </TextField>
              </FormControl>
            </Grid>

            {formik.values.emirateOption === "Others" && (
              <Grid item className="form-lable" xs={12} lg={6} md={6}>
                <TextField
                  name="emirate"
                  label="Please specify Location"
                  placeholder="Enter location"
                  fullWidth
                  value={formik.values.emirate}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.emirate && Boolean(formik.errors.emirate)
                  }
                  helperText={formik.touched.emirate && formik.errors.emirate}
                />
              </Grid>
            )}
            <Grid item className="form-lable" xs={12} lg={6} md={6}>
              <TextField
                name="po_box"
                type="number"
                label="P.O.Box"
                fullWidth
                value={formik.values.po_box}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.po_box && Boolean(formik.errors.po_box)}
                helperText={formik.touched.po_box && formik.errors.po_box}
              />
            </Grid>
            <Grid item className="form-lable" xs={12} lg={6} md={6}>

              <TextField
                name="phone_number"
                type="text"
                label="Phone Number"
                fullWidth
                inputProps={{
                  inputMode: "numeric",
                }}
                value={formik.values.phone_number}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.phone_number &&
                  Boolean(formik.errors.phone_number)
                }
                helperText={
                  formik.touched.phone_number && formik.errors.phone_number
                }
              />
            </Grid>
            <Grid item className="form-lable" xs={12} lg={6} md={6}>
              <TextField
                name="email"
                type="text"
                label="Customer Email Id"
                fullWidth
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
            </Grid>
            <Grid item className="form-lable" xs={12} lg={6} md={6}>
              <TextField
                name="vat_no"
                label="VAT No."
                fullWidth
                inputProps={{ inputMode: "numeric", maxLength: 15 }}
                value={formik.values.vat_no}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.vat_no && Boolean(formik.errors.vat_no)}
                helperText={formik.touched.vat_no && formik.errors.vat_no}
              />
            </Grid>

            {/* Global Credit Days field removed (commented out, not deleted)
              — credit terms are now only ever granted through the per-user
              request/approval flow (tbl_credit_approval_requests); the
              backend no longer honors tbl_customers.credit_days at all for
              invoice creation, so setting it here would be misleading. */}
            {/* {isAdmin && (
              <Grid item className="form-lable" xs={12} lg={6} md={6}>
                <TextField
                  name="credit_days"
                  type="number"
                  label="Credit Days"
                  fullWidth
                  inputProps={{ inputMode: "numeric", min: 0 }}
                  value={formik.values.credit_days}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.credit_days &&
                    Boolean(formik.errors.credit_days)
                  }
                  helperText={
                    (formik.touched.credit_days && formik.errors.credit_days) ||
                    "Only Admin can set this. Leave blank if not applicable yet."
                  }
                />
              </Grid>
            )} */}

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Status"
                name="status"
                value={formik.values.status}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.status && Boolean(formik.errors.status)}
                helperText={formik.touched.status && formik.errors.status}
              >
                {status?.map((option) => (
                  <MenuItem value={option.id}>{option.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          {/* Button */}
          <Grid container className="submit-button" alignItems="center">
            <Grid
              item
              xs={12}
              lg={12}
              md={12}
              display="flex"
              justifyContent="center"
            >
              <LoadingButton
                size="large"
                variant="contained"
                color="primary"
                type="submit"
                disabled={formik.isSubmitting}
              >
                Submit
              </LoadingButton>
            </Grid>
          </Grid>
        </form>
      </Grid>
    </Layout>
  );
};

export default ManageCustomer;
